import { useState } from 'react';
import {
  basemapPreviewTileUrls,
  type BasemapDefinition,
} from '@/engines/maplibre/basemaps';
import { Icon, tokens } from '@/design';

export interface BasemapPreviewProps {
  basemap: BasemapDefinition;
}

/**
 * Small, non-interactive map preview used by basemap gallery cards.
 *
 * Multi-layer basemaps stack their base and reference tiles, matching the
 * live map. A neutral placeholder remains visible if a provider is offline.
 */
export function BasemapPreview({ basemap }: BasemapPreviewProps) {
  const [failedLayers, setFailedLayers] = useState<ReadonlySet<number>>(
    () => new Set(),
  );
  const previewUrls = basemapPreviewTileUrls(basemap.id);

  return (
    <span
      aria-hidden="true"
      data-testid={`basemap-preview-${basemap.id}`}
      style={{
        position: 'relative',
        display: 'grid',
        placeItems: 'center',
        width: '100%',
        aspectRatio: '16 / 9',
        overflow: 'hidden',
        background: tokens.color.surface[2],
        color: tokens.color.ink.secondary,
      }}
    >
      <Icon name={basemap.thumbnailIcon} size={28} decorative />
      {previewUrls.map((url, index) =>
        failedLayers.has(index) ? null : (
          <img
            key={url}
            src={url}
            alt=""
            loading="lazy"
            decoding="async"
            // Lowercase DOM attribute — React SSR warns on camelCase fetchPriority.
            {...{ fetchpriority: 'low' }}
            draggable={false}
            onError={() =>
              setFailedLayers((current) => {
                const next = new Set(current);
                next.add(index);
                return next;
              })
            }
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          />
        ),
      )}
    </span>
  );
}
